"use client";

import { Input as InputPrimitive } from "@base-ui/react/input";
import type * as React from "react";
import { cn } from "@/lib/utils";

export type InputProps = Omit<
  InputPrimitive.Props & React.RefAttributes<HTMLInputElement>,
  "size"
> & {
  size?: "sm" | "default" | "lg" | number;
  unstyled?: boolean;
  nativeInput?: boolean;
  /** Extra classes for the inner <input> (the outer control owns the height). */
  inputClassName?: string;
};

export function Input({
  className,
  inputClassName,
  size = "default",
  unstyled = false,
  nativeInput = false,
  ...props
}: InputProps): React.ReactElement {
  // Height lives on the OUTER control so `h-12`-style overrides stretch the
  // whole field; the inner input is always `h-full` so text stays vertically
  // centred no matter the field height.
  const inputClassNameFinal = cn(
    "h-full w-full min-w-0 flex-1 rounded-[inherit] bg-transparent px-[calc(--spacing(3)-1px)] outline-none [transition:background-color_5000000s_ease-in-out_0s] placeholder:text-muted-foreground/72",
    size === "sm" && "px-[calc(--spacing(2.5)-1px)]",
    props.type === "search" &&
      "[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none",
    props.type === "file" &&
      "text-muted-foreground file:me-3 file:bg-transparent file:font-medium file:text-foreground file:text-sm",
    inputClassName,
  );

  return (
    <span
      className={
        cn(
          !unstyled &&
            "relative inline-flex h-8.5 w-full items-center rounded-lg border border-input bg-background not-dark:bg-clip-padding text-base text-foreground shadow-xs/5 ring-ring/24 transition-shadow before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] not-has-disabled:not-has-focus-visible:not-has-aria-invalid:before:shadow-[0_1px_--theme(--color-black/4%)] has-focus-visible:has-aria-invalid:border-destructive/64 has-focus-visible:has-aria-invalid:ring-destructive/16 has-aria-invalid:border-destructive/36 has-focus-visible:border-ring has-autofill:bg-foreground/4 has-disabled:opacity-64 has-[:disabled,:focus-visible,[aria-invalid]]:shadow-none has-focus-visible:ring-[3px] sm:h-7.5 sm:text-sm dark:bg-input/32 dark:has-autofill:bg-foreground/8 dark:has-aria-invalid:ring-destructive/24 dark:not-has-disabled:not-has-focus-visible:not-has-aria-invalid:before:shadow-[0_-1px_--theme(--color-white/6%)]",
          size === "sm" && !unstyled && "h-7.5 sm:h-6.5",
          size === "lg" && !unstyled && "h-9.5 sm:h-8.5",
          className,
        ) || undefined
      }
      data-size={size}
      data-slot="input-control"
    >
      {nativeInput ? (
        (() => {
          const { style, ...rest } = props;
          return (
            <input
              className={inputClassNameFinal}
              data-slot="input"
              size={typeof size === "number" ? size : undefined}
              style={typeof style === "function" ? undefined : style}
              {...rest}
            />
          );
        })()
      ) : (
        <InputPrimitive
          className={inputClassNameFinal}
          data-slot="input"
          size={typeof size === "number" ? size : undefined}
          {...props}
        />
      )}
    </span>
  );
}

export { InputPrimitive };
